import { useEffect, useRef, useState } from 'react';
import { getTrimmedAudioDuration } from '@src/services/project';
import {
  measureAll,
  getMeasuredAudioDuration,
} from '@src/services/audioDuration';
import { TRIM_EPSILON_SEC } from '@src/constants/timeline';

const setsEqual = (a, b) =>
  a.size === b.size && Array.from(a).every((value) => b.has(value));

/**
 * Whether a block's audio is trimmed — the SAME rule playback applies
 * (`getTrimmedAudioDuration` = MIN(slot, audio)): trimmed when the raw audio is
 * longer than that effective duration, i.e. the tail is cut to fit the slot. The
 * epsilon ignores sub-frame differences left by the shrink-to-fit realignment
 * (`getAudioAdjustedBlockEndTime`). Pure.
 */
export const isBlockTrimmed = (block, audioDurationSec) => {
  if (!audioDurationSec || audioDurationSec <= 0) return false;
  const effectiveSec = getTrimmedAudioDuration({
    startTime: block.startTime,
    endTime: block.endTime,
    audioDuration: audioDurationSec,
  });
  return audioDurationSec - effectiveSec > TRIM_EPSILON_SEC;
};

// Trimmed block ids, from whatever durations are currently cached (unmeasured
// blocks simply don't count yet — they flip in once their measurement settles).
const collectTrimmedIds = (sources) => {
  const ids = new Set();
  sources.forEach((source) => {
    const duration = getMeasuredAudioDuration(source.audioLink);
    if (duration != null && isBlockTrimmed(source, duration)) {
      ids.add(source.id);
    }
  });
  return ids;
};

/**
 * Which voiceover blocks have trimmed audio. Measures each block's audio
 * duration once (bounded concurrency, cached + deduped by URL) and returns a Set
 * of trimmed block ids that fills in as measurements settle. Uses the same trim
 * rule as playback, and only reads metadata — never touches the audio players.
 *
 * @param {{ id: string, audioLink: string, startTime: string, endTime: string }[]} sources
 *   Blocks with valid, non-expired audio (built by the caller).
 * @returns {Set<string>} ids of blocks whose audio is trimmed
 */
const useBlockTrimFlags = (sources) => {
  const [trimmedIds, setTrimmedIds] = useState(() => new Set());
  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;

  // Re-run only when the inputs that affect the result change (block added,
  // audio re-synthesized, slot resized) — not on every unrelated parent render.
  const signature = sources
    .map((s) => `${s.id}:${s.audioLink}:${s.startTime}:${s.endTime}`)
    .join('|');

  useEffect(() => {
    let cancelled = false;
    const refresh = () =>
      setTrimmedIds((prev) => {
        const next = collectTrimmedIds(sourcesRef.current);
        return setsEqual(prev, next) ? prev : next;
      });

    refresh(); // instant for anything already measured
    // Measure only the not-yet-known URLs, refreshing as EACH settles so markers
    // appear progressively — otherwise a few-hundred-block Fit view would show
    // nothing until the whole queue finishes. Already-pending URLs are reused
    // (deduped) by measureDuration, not re-requested.
    const urls = sourcesRef.current
      .map((s) => s.audioLink)
      .filter((url) => url && getMeasuredAudioDuration(url) === undefined);
    measureAll(urls, () => {
      if (!cancelled) refresh();
    });

    return () => {
      cancelled = true;
    };
  }, [signature]);

  return trimmedIds;
};

export default useBlockTrimFlags;
